// Shared logic for a mentor's consultation offering (free clarity call / paid session /
// both). Pure + unit-tested. Schema on the mentor doc:
//   consultation: { type: 'free' | 'paid' | 'both', description?: string, updatedAt }
// Mentors without the field default to 'free' (preserves the original behaviour where
// every listing offered a free clarity call).

export type ConsultationType = 'free' | 'paid' | 'both';
export const CONSULTATION_TYPES: ConsultationType[] = ['free', 'paid', 'both'];
export const CONSULTATION_MAX_WORDS = 300;
export const CONSULTATION_MAX_CHARS = 2400; // paste-bomb safety cap

// Migration-safe read: anything that isn't 'paid'/'both' (including missing) → 'free'.
export function normalizeConsultationType(v: unknown): ConsultationType {
  return v === 'paid' || v === 'both' ? v : 'free';
}

// Word count used by the editor counter + hard cap. Matches the brief's helper exactly.
export function wordCount(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

// Trim input to the word cap (keeps the first N words). Used to hard-cap the textarea.
export function clampToWords(s: string, maxWords: number = CONSULTATION_MAX_WORDS): string {
  const parts = s.split(/(\s+)/); // keep separators so typing spaces feels natural
  let words = 0;
  let out = '';
  for (const tok of parts) {
    if (/\S/.test(tok)) {
      if (words >= maxWords) break;
      words++;
    }
    out += tok;
  }
  return out;
}

// Single consultation pill — one indicator everywhere (card + profile), dynamic by type.
// (Replaces the earlier two-pill design that showed a type badge AND a separate clarity-
// call pill, which read as contradictory for paid mentors.)
export function consultationLabel(type?: ConsultationType): string {
  switch (normalizeConsultationType(type)) {
    case 'paid': return '💼 Paid sessions available';
    case 'both': return '💬💼 Free clarity call + paid sessions';
    default: return '💬 Free 20-min clarity call';
  }
}

// Subtle, distinct background per type. Solid colours (no gradients — they look noisy on
// mobile). 'both' uses teal to read as distinct from free-green and paid-blue.
export function consultationColors(type?: ConsultationType): { bg: string; fg: string } {
  switch (normalizeConsultationType(type)) {
    case 'paid': return { bg: '#e3f2fd', fg: '#1565c0' };
    case 'both': return { bg: '#e0f2f1', fg: '#00695c' };
    default: return { bg: '#e8f5e9', fg: '#2e7d32' };
  }
}

// Directory filter semantics:
//   filter 'free' → mentor offers free  (free OR both)
//   filter 'paid' → mentor offers paid  (paid OR both)
//   filter 'both' → mentor offers BOTH  (both only)
export function consultationMatches(filter: ConsultationType, mentorType: ConsultationType): boolean {
  if (filter === 'free') return mentorType === 'free' || mentorType === 'both';
  if (filter === 'paid') return mentorType === 'paid' || mentorType === 'both';
  return mentorType === 'both';
}

// Booking-form heading by type. For 'both' the form shows a session-type toggle, so the
// heading is resolved from the toggle's current selection ('free' | 'paid').
export function bookingTitle(type: 'free' | 'paid'): string {
  return type === 'paid' ? 'Request a paid session' : 'Request a free 20-min clarity call';
}
